import { useEffect, useRef, useState } from "react";
import { routeOffer } from "../../../services/api/offers";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery } from "@tanstack/react-query";
import { showToast } from "../../../store/toast/toastSlice";
import BaseButton from "../../../components/ui/BaseButton";
import { Avatar } from "@mui/material";
import BaseRating from "../../../components/ui/BaseRating";
import { fetchNearbyFreelancers } from "../../../services/api/user";
import BaseSelect from "../../../components/ui/BaseSelect";
import BaseTextField from "../../../components/ui/BaseTextField";
import { setWorkOrderDetail } from "../../../store/workOrder/workOrderSlice";
import { useUpdateWorkOrder } from "../../../hooks/useUpdateWorkOrder";
import BaseDataTableV2 from "../../../components/ui/BaseDataTableV2";
import { debounce, formatPhoneNumber } from "../../../utils/helpers";
import { CurrencySymbol, PaymentType, ProviderRowType } from "../../../utils/enum";
import { sortByPaymentType } from "../../../utils/sortByPaymentType";
import { fetchTalentPool } from "../../../services/api/talentPool";
import BaseTooltip from "../../../components/ui/BaseTooltip";
import BaseCheckbox from "../../../components/ui/BaseCheckbox";
import OpenableLink from "../../../components/ui/OpenableLink";

const NearbyFreelancers = ({ filters, radius, setRadius, refetchProvidersCount, mode }) => {
  const { updateWorkOrder } = useUpdateWorkOrder();
  const workOrderDetail = useSelector((state) => state.workOrder.workOrderDetails);
  const [routeUserId, setRouteUserId] = useState(null);
  const [selectedTalentPool, setSelectedTalentPool] = useState("");
  const [filterByRequiredSkills, setFilterByRequiredSkills] = useState(false);
  const [filterByRequiredTools, setFilterByRequiredTools] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isBulkRouting, setIsBulkRouting] = useState(false);

  const dispatch = useDispatch();
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [queryParams, setQueryParams] = useState({
    page: 1,
    limit: 10,
    search: "",
    field: "personalAddress",
    order: "asc",
  });
  const [search, setSearch] = useState("");

  const {
    isPending: isNearbyApiLoading,
    error: isNearbyError,
    data: nearbyFreelancersData,
    refetch: refetchNearbyFreelancers,
  } = useQuery({
    queryKey: [
      "nearbyFreelancers",
      queryParams.page,
      queryParams.limit,
      queryParams.search,
      queryParams.field,
      queryParams.order,
      queryParams.talentPoolId,
      radius,
      filterByRequiredSkills,
      filterByRequiredTools,
    ],
    queryFn: () =>
      fetchNearbyFreelancers({
        radius,
        latitude: workOrderDetail?.address?.latitude,
        longitude: workOrderDetail?.address?.longitude,
        workOrderId: workOrderDetail?.id,
        limit: queryParams?.limit,
        page: queryParams?.page,
        talentPoolId: queryParams?.talentPoolId,
        mode,
        search: queryParams?.search,
        ...(queryParams.field && queryParams.order
          ? { sortBy: queryParams.field, sortOrder: queryParams.order }
          : {}),
        filterByRequiredSkills,
        filterByRequiredTools,
      }),
  });

  const {
    isPending: isRouteLoading,
    error: isRouteError,
    mutateAsync: routeOfferMutation,
  } = useMutation({
    mutationFn: routeOffer,
    onSuccess: (data) => {
      //Updating the work order details in store so we don't have to fetch the latest details again from backend
      dispatch(
        setWorkOrderDetail({
          ...workOrderDetail,
          offersCount: {
            ...workOrderDetail.offersCount,
            routed: workOrderDetail.offersCount.routed + 1,
          },
        }),
      );
      dispatch(
        showToast({
          message: "Job Routed Successfully",
          type: "success",
        }),
      );
    },
  });
  const { mutateAsync: routeOfferBulkMutation } = useMutation({
    mutationFn: routeOffer,
  });

  const {
    isPending: isFetchTalentPoolApiLoading,
    data: talentPoolData,
    refetch: refetchTalentPool,
  } = useQuery({
    queryKey: ["talentPool"],

    queryFn: () => fetchTalentPool(),
    keepPreviousData: true,
    enabled: mode === "talentPool",
  });
  const talentPoolOptions =
    talentPoolData?.data?.data?.map((pool) => ({
      label: `${pool.name}`,
      value: pool.id,
    })) || [];

  async function handleRouteButton(userId) {
    try {
      setRouteUserId(userId);
      let payload = { workOrderId: workOrderDetail.id, userId };
      const routedResponse = await routeOfferMutation(payload);
      refetchNearbyFreelancers();
      refetchProvidersCount();
      updateWorkOrder();
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message,
          type: "error",
        }),
      );
    } finally {
      setRouteUserId(null);
    }
  }

  async function handleBulkRoute() {
    if (!selectedRows.length) return;
    setIsBulkRouting(true);
    try {
      await routeOfferBulkMutation({ workOrderId: workOrderDetail.id, userIds: selectedRows });
      dispatch(
        setWorkOrderDetail({
          ...workOrderDetail,
          offersCount: {
            ...workOrderDetail.offersCount,
            routed: workOrderDetail.offersCount.routed + selectedRows.length,
          },
        }),
      );
      dispatch(
        showToast({
          message: `${selectedRows.length} engineer${selectedRows.length !== 1 ? "s" : ""} routed successfully`,
          type: "success",
        }),
      );
      setSelectedRows([]);
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message || "Failed to route the selected engineers",
          type: "error",
        }),
      );
    } finally {
      refetchNearbyFreelancers();
      refetchProvidersCount();
      updateWorkOrder();
      setIsBulkRouting(false);
    }
  }

  const handleRadiusChange = (e) => {
    setRadius(e.target.value);
  };

  const handlePaginationChange = (newPaginationModel) => {
    setPaginationModel(newPaginationModel);
    setQueryParams((prev) => ({
      ...prev,
      page: newPaginationModel?.page + 1,
      limit: newPaginationModel?.pageSize,
    }));
  };
  const handleSortModelChange = (newSortModel) => {
    if (newSortModel.length > 0) {
      setQueryParams((prev) => ({
        ...prev,
        field: newSortModel[0].field,
        order: newSortModel[0].sort,
      }));
    }
  };
  const debouncedSearchRef = useRef(
    debounce((val) => {
      setQueryParams((prev) => ({
        ...prev,
        search: val,
        page: 1,
      }));
    }, 500),
  );
  const handleTalentPoolChange = (e) => {
    const value = e.target.value;

    setSelectedTalentPool(value);
    setQueryParams((prev) => ({
      ...prev,
      page: 1,
      talentPoolId: value,
    }));
  };

  const data = nearbyFreelancersData?.data?.users || [];

  const COLUMNS = [
    ...(![
      "DRAFT",
      "APPROVED",
      "INPROGRESS",
      "ASSIGNED",
      "WORKDONE",
      "VERIFIED",
      "INVOICED",
      "PAID",
      "CANCELLED",
      "INCOMPLETE",
    ].includes(workOrderDetail.status)
      ? [
          {
            field: "action",
            headerName: "Action",
            sortable: false,
            flex: 0.7,
            renderCell: (params) => (
              <div className="flex overflow-auto h-10 items-center">
                <BaseButton
                  loaderSize={18}
                  loaderColor="primary"
                  isLoading={routeUserId === params.row?.id}
                  style={{
                    boxShadow: "0px 0px 4px 0px rgba(0, 0, 0, 0.15)",
                    paddingTop: "0px",
                    paddingBottom: "0px",
                  }}
                  className="bg-white hover:bg-primary hover:text-white text-primary mr-3 ml-0.5 w-[70px]"
                  onClick={() => handleRouteButton(params?.row?.id)}
                >
                  Route
                </BaseButton>
              </div>
            ),
          },
        ]
      : []),
    {
      field: "id",
      headerName: "ID",
      sortable: true,
      flex: 0.5,
      // A company row stands in for the whole company, so it carries the company ID —
      // matching the ID shown for that company elsewhere on the work order.
      renderCell: (params) => (
        <p className="text-primary">
          {params?.row?.type === ProviderRowType.FREELANCE_COMPANY
            ? params?.row?.companyId
            : params?.value}
        </p>
      ),
    },
    {
      field: "engineer",
      headerName: "Name",
      sortable: false,
      flex: 0.7,
      renderCell: (params) => {
        if (params.row.type === ProviderRowType.FREELANCE_COMPANY) {
          return (
            <div className="flex items-center gap-2">
              <Avatar sx={{ bgcolor: "#2561A92A", width: 30, height: 30 }}>
                <i className="fa-solid fa-building text-primary text-xs"></i>
              </Avatar>
              <div>
                <OpenableLink
                  as="p"
                  to={`/user/details/profile/${params.row.id}`}
                  className="text-primary font-medium capitalize cursor-pointer hover:underline"
                >
                  {params.row.name}
                </OpenableLink>
                <p className="text-xs text-[#A5A5A5]">{params.row.engineerCount} engineer{params.row.engineerCount !== 1 ? "s" : ""} nearby</p>
              </div>
            </div>
          );
        }
        return (
          <OpenableLink
            as="div"
            to={`/user/details/profile/${params?.row?.id}`}
            className="flex items-center cursor-pointer"
          >
            <Avatar src={params?.row?.profile} sx={{ width: 30, height: 30 }} />
            <div className="ml-2">
              <p className="text-primary capitalize">
                {params?.row?.isCompanyOwner
                  ? params?.row?.company?.name
                  : `${params?.row?.firstName} ${params?.row?.lastName}`}
              </p>
              <p className="text-xs">
                {params?.row?.isCompanyOwner
                  ? `${params?.row?.firstName} ${params?.row?.lastName}`
                  : formatPhoneNumber(params?.row?.phoneNumber)}
              </p>
            </div>
          </OpenableLink>
        );
      },
    },
    {
      field: "billingRates",
      headerName: "Payment Rates",
      sortable: false,
      width: 300,
      renderCell: (params) => {
        if (params.row.type === ProviderRowType.FREELANCE_COMPANY) return <p className="text-[#A5A5A5] text-xs">—</p>;
        const sortedRates = sortByPaymentType(params.value);
        return sortedRates?.map((rate) => (
          <div className="flex flex-wrap table-font-size w-full">
            <div>
              <p className="h-[14px] text-primary text-center text-[11px] font-medium">
                {`${rate.rate} ${CurrencySymbol[rate.currency]}` || ""}
              </p>
              <p className="text-center">{PaymentType[rate.paymentType]}</p>
            </div>
          </div>
        ));
      },
    },
    {
      field: "talentPools",
      headerName: "Talent Pool",
      sortable: false,
      flex: 0.5,
      renderCell: (params) => {
        if (params.row.type === ProviderRowType.FREELANCE_COMPANY) return <p className="text-[#A5A5A5] text-xs">—</p>;
        const pools = params?.row?.talentPools || [];
        return (
          <div className="flex flex-wrap font-normal table-font-size">
            {pools.map((p, index) => (
              <span key={p.id}>
                <OpenableLink
                  to={`/workForce/talentPool/engineers/${p.id}`}
                  className="text-primary cursor-pointer hover:underline"
                >
                  {p.name}
                </OpenableLink>
                {index < pools.length - 1 && ", "}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      field: "userSkills",
      headerName: "Skills",
      sortable: false,
      flex: 0.5,
      renderCell: (params) => {
        if (params.row.type === ProviderRowType.FREELANCE_COMPANY) return <p className="text-[#A5A5A5] text-xs">—</p>;
        const skills = params.row.userSkills?.map((skill) => skill.subCategory.subCategory) || [];
        const skillsText = skills.join(", ");
        return (
          <BaseTooltip
            position="bottom"
            interactive={true}
            content={
              <div className="relative pb-2">
                <p className="text-white">{skillsText}</p>
              </div>
            }
            appendTo={document.body}
          >
            <p className="table-font-size truncate max-w-72 overflow-hidden whitespace-nowrap">
              {skillsText}
            </p>
          </BaseTooltip>
        );
      },
    },
    {
      field: "privateFeedbackCount",
      headerName: "Private Feedback",
      sortable: false,
      flex: 0.5,
      renderCell: (params) => {
        if (params.row.type === ProviderRowType.FREELANCE_COMPANY) return <p className="text-[#A5A5A5] text-xs">—</p>;
        return <p>{params?.value}</p>;
      },
    },
    {
      field: "averageRating",
      headerName: "Ratings",
      sortable: false,
      flex: 0.5,
      renderCell: (params) => {
        if (params.row.type === ProviderRowType.FREELANCE_COMPANY) return <p className="text-[#A5A5A5] text-xs">—</p>;
        return (
          <div className="flex items-center">
            <BaseRating rating={params?.value} />
            <p className="ml-3">{params.value || ""}</p>
          </div>
        );
      },
    },
    {
      field: "workedWithJobsCount",
      headerName: "Jobs",
      flex: 0.5,
      renderCell: (params) => {
        if (params.row.type === ProviderRowType.FREELANCE_COMPANY) return <p className="text-[#A5A5A5] text-xs">—</p>;
        return (
          <div className="table-font-size">
            {params.row.workedWith ? (
              <p className="text-primary font-medium">
                {params.row.workedWithJobsCount}{" "}
                <span className="text-[11px] font-300">(My Company)</span>
              </p>
            ) : (
              <p className="font-medium">
                {params.row.marketplaceJobsCount}
                <span className="text-[11px] font-300"> (Marketplace)</span>
              </p>
            )}
          </div>
        );
      },
    },
    {
      field: "personalAddress",
      headerName: "Distance",
      sortable: true,
      flex: 0.5,
      renderCell: (params) => (
        <div className="flex items-center">
          <i className="fa-light fa-car text-xs"></i>
          <p className="text-primary ml-2">{params?.value?.distance_km} Km</p>
        </div>
      ),
    },
    {
      field: "cancellationRate",
      headerName: "Cancelation",
      sortable: false,
      flex: 0.5,
      renderCell: (params) => {
        if (params.row.type === ProviderRowType.FREELANCE_COMPANY) return <p className="text-[#A5A5A5] text-xs">—</p>;
        return (
          <div className="flex items-center">
            <p className="ml-1">{params?.row?.cancellationRate} %</p>
          </div>
        );
      },
    },
    {
      field: "timelinessRate",
      headerName: "Timeliness",
      sortable: false,
      flex: 0.5,
      renderCell: (params) => {
        if (params.row.type === ProviderRowType.FREELANCE_COMPANY) return <p className="text-[#A5A5A5] text-xs">—</p>;
        return <div className="flex items-center">{params?.row?.timelinessRate} %</div>;
      },
    },
  ];

  return (
    <>
      <div className="mb-3 grid grid-cols-12 items-center">
        <div className="col-span-12 lg:col-span-8 flex flex-wrap items-center gap-4">
          <div className="flex items-center">
            <p className="mr-2 mt-1 whitespace-nowrap">Distance:</p>
            <BaseSelect
              className="w-[150px] rounded-lg "
              fullHeight={true}
              fullWidth
              labelShrink={false}
              required={true}
              id="workOrderManagerId"
              name="workOrderManagerId"
              value={radius}
              onChange={handleRadiusChange}
              options={[
                {
                  label: "50 km",
                  value: 50,
                },
                {
                  label: "100 km",
                  value: 100,
                },
                {
                  label: "150 km",
                  value: 150,
                },
                {
                  label: "500 km",
                  value: 500,
                },
                {
                  label: "1000 km",
                  value: 1000,
                },
              ]}
            />
          </div>
          {mode === "talentPool" && (
            <div className="flex items-center">
              <p className="mr-2 mt-1 whitespace-nowrap">Talent Pool:</p>

              <BaseSelect
                className="w-[200px] rounded-lg"
                fullHeight
                fullWidth
                labelShrink={false}
                required
                id="talentPoolId"
                name="talentPoolId"
                value={selectedTalentPool}
                onChange={handleTalentPoolChange}
                options={talentPoolOptions}
                placeholder="Select Talent Pool"
              />
            </div>
          )}

          <div className="flex items-center md:mt-0 mt-4">
            <p className="mr-2">Providers:</p>
            <p className="ml-2 bg-[#EFF4FF] px-5 py-1 rounded-md">
              {nearbyFreelancersData?.data?.totalCount || 0}
            </p>
          </div>
          <div className="flex items-center md:mt-0 mt-4 ml-2">
            <BaseCheckbox
              label="Filter by Skills"
              id="userSkills"
              name="userSkills"
              checked={filterByRequiredSkills}
              onChange={(e) => setFilterByRequiredSkills(e.target.checked)}
            />
          </div>
          <div className="flex items-center md:mt-0 mt-4 ml-2">
            <BaseCheckbox
              label="Filter by Tools"
              id="userTools"
              name="userTools"
              checked={filterByRequiredTools}
              onChange={(e) => setFilterByRequiredTools(e.target.checked)}
            />
          </div>
          {!["DRAFT", "APPROVED", "INPROGRESS", "ASSIGNED", "WORKDONE", "VERIFIED", "INVOICED", "PAID", "CANCELLED", "INCOMPLETE"].includes(workOrderDetail.status) &&
            selectedRows.length > 0 && (
              <BaseButton
                isLoading={isBulkRouting}
                loaderSize={18}
                loaderColor="white"
                onClick={handleBulkRoute}
                style={{ boxShadow: "0px 0px 4px 0px rgba(0, 0, 0, 0.15)", paddingTop: "0px", paddingBottom: "0px" }}
                className="bg-primary text-white"
              >
                Route Selected ({selectedRows.length})
              </BaseButton>
            )}
        </div>
        <div className="col-span-12 lg:col-span-4 flex justify-end">
          <BaseTextField
            placeholder="Search"
            onChange={(e) => {
              const val = e.target.value;
              setSearch(val);
              debouncedSearchRef.current(val);
            }}
            value={search}
            startAdornment={<i className="fa-light fa-magnifying-glass text-lg" />}
            className="w-full sm:w-[300px] lg:w-[350px]"
          />
        </div>
      </div>

          <BaseDataTableV2
            className="max-h-[calc(100vh-300px)] overflow-auto"
            rowHeight={45}
            columns={COLUMNS}
            data={data || []}
            isLoading={isNearbyApiLoading}
            disableColumnResize={false}
            checkboxSelection
            rowSelectionModel={selectedRows}
            onRowSelectionModelChange={(newSelection) => setSelectedRows(newSelection)}
            hideFooter={false}
            disableColumnSorting={false}
            pagination
            rowCount={nearbyFreelancersData?.data?.totalCount}
            pageSizeOptions={[10, 20, 50, 100]}
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationChange}
            paginationMode="server"
            sortingMode="server"
            sortModel={
              queryParams.field && queryParams.order
                ? [{ field: queryParams.field, sort: queryParams.order }]
                : []
            }
            onSortModelChange={handleSortModelChange}
            sortingOrder={["asc", "desc"]}
          />
    </>
  );
};

export default NearbyFreelancers;
